import React, { memo, useContext } from 'react'

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { values } from 'lodash'
import { colors } from 'src/assets/colors'
import { Label } from 'src/components/__legacy__/form/label'
import { Spacer } from 'src/components/__legacy__/spacer/spacer'
import { Add, ArrowDown, ArrowUp, Close } from 'src/components/icon'
import { toggleRoles } from 'src/legacy_modules/entities/member/entity'
import {
  DataType,
  ItemType,
  SmallItem,
} from 'src/legacy_modules/entities/small-item/entity'
import {
  itemTypeToDateFormat,
  itemTypeToTextLabel,
} from 'src/legacy_modules/entities/small-item/util'
import { itemTypeToText, rolesToText } from 'src/legacy_modules/text'
import { isEmptyLetter } from 'src/legacy_modules/util/is-empty-letter'
import styled from 'styled-components'
import {
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
  width,
  WidthProps,
} from 'styled-system'

import { VisibilityIcon } from './common/visibility-icon'
import { ChoiceComponent } from './components/choice'
import {
  DateTypeSelect,
  ItemTypeSelect,
  TextTypeSelect,
} from './components/item-type-select'
import { RoleCheckbox } from './components/role-checkbox'
import { RolePermissionSection } from './components/role-permission-section'
import { CardContext, CardContextValue } from './context'
import { dataTypeRadioItems } from './options'
import {
  getGeneralItemType,
  hasChoice,
  isDateAndTimeType,
  isLabelTypeWithEdc,
  isTextType,
} from '../../../../../../../util/item-type-classification'

type Props = Omit<
  {
    smallItem: SmallItem
    v2: boolean
    canChangeSmallItemIndex: ({ smallItemUid }: { smallItemUid: string }) => {
      canUp: boolean
      canDown: boolean
    }
    hideRolePermission: boolean
  } & CardContextValue,
  'isClinicalInParent'
>

const ItemContainer = styled.div`
  width: 100%;
`

const ItemsContainer = styled(Flex)`
  /* justify-content: flex-start; */
  flex-wrap: wrap;
  width: 100%;
`

const Item = styled.div<SpaceProps & WidthProps & LayoutProps & FlexboxProps>`
  ${space};
  ${width};
  ${layout};
  ${flexbox};
`

const Value = styled.div`
  padding: 6px 0;
  font-size: 14px;
  min-width: 150px;
  border-bottom: 1px solid ${colors.borderGray};
  color: ${colors.dark};
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const shouldShowError = (value: any) => {
  if (typeof value === 'number') {
    return value < 0
  }

  return !value
}

export const Card = memo<Props>(function Card(props) {
  const {
    smallItem,
    smallItemMap,
    releaseTried,
    v2,
    onAddChoice,
    onRemoveSmallItem,
    onRemoveChoice,
    onChangeSmallItem,
    canChangeSmallItemIndex,
    onChangeSmallItemIndex,
    onChangeChoice,
    onAddSmallItemToChoice,
    hideRolePermission,
  } = props

  const { canUp, canDown } = canChangeSmallItemIndex({
    smallItemUid: smallItem.uid,
  })

  return (
    <CardContext.Provider
      value={{
        isClinicalInParent: smallItem.dataType === DataType.Clinical,
        releaseTried,
        smallItemMap,
        onChangeSmallItem,
        onChangeSmallItemIndex,
        onRemoveSmallItem,
        onAddChoice,
        onRemoveChoice,
        onChangeChoice,
        onAddSmallItemToChoice,
      }}
    >
      <HStack spacing="1" align="center" w="full">
        <Stack>
          <IconButton
            icon={<ArrowUp />}
            variant="customIconButtonGhost"
            aria-label="フィールドを上に移動する"
            isDisabled={!canUp}
            onClick={() =>
              onChangeSmallItemIndex({
                smallItemUid: smallItem.uid,
                direction: 'up',
              })
            }
          />

          <IconButton
            icon={<ArrowDown />}
            variant="customIconButtonGhost"
            aria-label="フィールドを下に移動する"
            isDisabled={!canDown}
            onClick={() =>
              onChangeSmallItemIndex({
                smallItemUid: smallItem.uid,
                direction: 'down',
              })
            }
          />
        </Stack>
        <Box
          id={smallItem.uid}
          key={smallItem.uid}
          border="1px"
          borderColor="gray.100"
          borderRadius="base"
          p="5"
          pos="relative"
          flex="1"
        >
          <WithoutChoiceComponent
            smallItem={smallItem}
            releaseTried={releaseTried}
            v2={v2}
            onChangeSmallItem={onChangeSmallItem}
          />

          {hasChoice(smallItem) && (
            <ChoiceList
              smallItem={smallItem}
              v2={v2}
              hideRolePermission={hideRolePermission}
            />
          )}

          {v2 &&
            !hideRolePermission &&
            smallItem.itemType !== ItemType.Label && (
              <>
                <Spacer size={4} horizontal />
                <RolePermissionSection
                  smallItem={smallItem}
                  releaseTried={releaseTried}
                  onChangeSmallItem={onChangeSmallItem}
                />
              </>
            )}

          <Spacer size={4} horizontal />

          {!smallItem.presetUsed && (
            <IconButton
              pos="absolute"
              top="3"
              right="3"
              icon={<Close />}
              aria-label="Delete Field"
              variant="customIconButtonGhost"
              onClick={() => onRemoveSmallItem({ smallItemUid: smallItem.uid })}
            />
          )}
        </Box>
      </HStack>
    </CardContext.Provider>
  )
})

type WithoutChoiceProps = {
  smallItem: SmallItem
  releaseTried: boolean
  v2: boolean
  onChangeSmallItem: (value: {
    smallItemUid: string
    values: { [key in keyof SmallItem]?: SmallItem[key] }
  }) => void
}

const WithoutChoiceComponent = memo<WithoutChoiceProps>(
  function WithoutChoiceComponent(props) {
    const { smallItem, releaseTried, v2, onChangeSmallItem } = props

    return (
      <Stack spacing="5">
        <ItemContainer>
          <Flex direction="row" justify="space-between" align="center">
            <Box>
              <FormControl
                isInvalid={releaseTried && shouldShowError(smallItem.title)}
              >
                <FormLabel>フィールド名</FormLabel>
                <Input
                  w="580px"
                  placeholder={
                    smallItem.itemType === ItemType.Label
                      ? 'ラベルを入力してください'
                      : 'フィールドのタイトルを入力してください'
                  }
                  value={smallItem.title}
                  onChange={e =>
                    onChangeSmallItem({
                      smallItemUid: smallItem.uid,
                      values: { title: e.target.value },
                    })
                  }
                />
              </FormControl>

              {smallItem.presetUsed && (
                <Text fontSize="xs" color="gray.400">
                  このフィールドは削除できません
                </Text>
              )}
            </Box>
          </Flex>
        </ItemContainer>

        {!v2 && (
          <Wrap spacing="12">
            <WrapItem display="box">
              <FormControl
                isInvalid={
                  releaseTried &&
                  smallItem.itemType !== ItemType.Label &&
                  smallItem.inputRoles.length === 0
                }
              >
                <FormLabel>ロール</FormLabel>
                {smallItem.presetUsed ? (
                  <Value>{rolesToText(smallItem.inputRoles)}</Value>
                ) : (
                  <RoleCheckbox
                    checkedItems={smallItem.inputRoles}
                    onChange={role => {
                      onChangeSmallItem({
                        smallItemUid: smallItem.uid,
                        values: {
                          inputRoles: toggleRoles(smallItem.inputRoles, role),
                        },
                      })
                    }}
                    disabled={smallItem.itemType === ItemType.Label}
                  />
                )}
              </FormControl>
            </WrapItem>

            <WrapItem>
              <FormControl>
                <FormLabel>EDC連携</FormLabel>
                <RadioGroup
                  value={smallItem.dataType.toString()}
                  onChange={dataType => {
                    onChangeSmallItem({
                      smallItemUid: smallItem.uid,
                      values: { dataType: Number(dataType) as DataType },
                    })
                  }}
                >
                  <HStack spacing="4">
                    {dataTypeRadioItems.map(item => (
                      <Radio
                        key={item.value}
                        value={item.value.toString()}
                        isChecked={item.value === smallItem.dataType}
                      >
                        {item.name}
                      </Radio>
                    ))}
                  </HStack>
                </RadioGroup>
              </FormControl>
            </WrapItem>

            <WrapItem display="block">
              <FormLabel>医療機関設定</FormLabel>
              <VisibilityIcon smallItem={smallItem} size={'large'} />
            </WrapItem>
          </Wrap>
        )}
        <Stack spacing="5">
          {smallItem.dataType === DataType.Clinical && !v2 && (
            <EDCItems
              smallItem={smallItem}
              releaseTried={releaseTried}
              onChangeSmallItem={onChangeSmallItem}
            />
          )}

          <FormControl>
            <FormLabel>フィールドの説明</FormLabel>
            <Input
              width="580px"
              value={smallItem.description}
              placeholder={
                smallItem.itemType === ItemType.Label
                  ? ''
                  : 'フィールドに関する説明文や回答例などを入力してください'
              }
              onChange={e =>
                onChangeSmallItem({
                  smallItemUid: smallItem.uid,
                  values: { description: e.target.value },
                })
              }
              isDisabled={smallItem.itemType === ItemType.Label}
            />
          </FormControl>
        </Stack>

        {!smallItem.presetUsed && (
          <Wrap spacing="4">
            <WrapItem>
              <Stack spacing="5">
                <FormControl>
                  <FormLabel>回答タイプ</FormLabel>
                  <ItemTypeSelect
                    selectedValue={getGeneralItemType(smallItem)}
                    onSelect={itemType => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      type FirstArg<T> = T extends (args: infer U) => any
                        ? U
                        : never
                      const values: FirstArg<
                        WithoutChoiceProps['onChangeSmallItem']
                      >['values'] = {
                        itemType,
                      }
                      if (itemType === ItemType.CheckBox) {
                        values.edcDataItem = '-'
                      }
                      // TODO:
                      if (itemType === ItemType.Label) {
                        if (!v2) {
                          values.inputRoles = []
                        } else {
                          values.rolePermissions = []
                        }
                        values.description = ''
                      }
                      onChangeSmallItem({
                        smallItemUid: smallItem.uid,
                        values,
                      })
                    }}
                  />
                </FormControl>
                {smallItem.itemType === ItemType.File && (
                  <Box>
                    <Checkbox
                      isChecked={smallItem.isCertifiedCopy}
                      onChange={e =>
                        onChangeSmallItem({
                          smallItemUid: smallItem.uid,
                          values: { isCertifiedCopy: e.target.checked },
                        })
                      }
                    >
                      Certified Copyとして扱う
                    </Checkbox>
                  </Box>
                )}
              </Stack>
            </WrapItem>

            {isDateAndTimeType(smallItem) && (
              <WrapItem>
                <FormControl>
                  <FormLabel>フォーマット</FormLabel>
                  <DateTypeSelect
                    selectedValue={smallItem.itemType}
                    onSelect={itemType => {
                      onChangeSmallItem({
                        smallItemUid: smallItem.uid,
                        values: { itemType },
                      })
                    }}
                  />
                </FormControl>
              </WrapItem>
            )}

            {isTextType(smallItem) && (
              <WrapItem>
                <FormControl>
                  <FormLabel>フォーマット</FormLabel>
                  <TextTypeSelect
                    selectedValue={smallItem.itemType}
                    onSelect={itemType => {
                      onChangeSmallItem({
                        smallItemUid: smallItem.uid,
                        values: { itemType },
                      })
                    }}
                  />
                </FormControl>
              </WrapItem>
            )}
          </Wrap>
        )}

        {smallItem.presetUsed && (
          <ItemsContainer>
            <Item mr={4} width={160}>
              <Label bold>回答タイプ</Label>
              <Spacer size={10} />
              <Value>{itemTypeToText(smallItem.itemType)}</Value>
            </Item>

            {isDateAndTimeType(smallItem) && (
              <Item mr={4} width={160}>
                <Label bold>フォーマット</Label>
                <Spacer size={10} />
                <Value>{itemTypeToDateFormat(smallItem.itemType)}</Value>
              </Item>
            )}

            {isTextType(smallItem) && (
              <Item mr={4} width={160}>
                <Label bold>フォーマット</Label>
                <Spacer size={10} />
                <Value>{itemTypeToTextLabel(smallItem.itemType)}</Value>
              </Item>
            )}
          </ItemsContainer>
        )}

        <Spacer size={20} />

        {smallItem.itemType === ItemType.Number && (
          <FormControl
            isInvalid={releaseTried && shouldShowError(smallItem.unit)}
          >
            <FormLabel>入力する数値の単位</FormLabel>
            {smallItem.presetUsed ? (
              <Item width={160}>
                <Text>{smallItem.unit}</Text>
              </Item>
            ) : (
              <Input
                width="40%"
                value={smallItem.unit}
                placeholder="℃"
                onChange={e => {
                  onChangeSmallItem({
                    smallItemUid: smallItem.uid,
                    values: { unit: e.target.value },
                  })
                }}
              />
            )}
          </FormControl>
        )}

        {isLabelTypeWithEdc(smallItem) && (
          <FormControl
            isInvalid={releaseTried && isEmptyLetter(smallItem.value)}
          >
            <FormLabel>EDC連携テキスト</FormLabel>
            <Textarea
              value={smallItem.value ?? ''}
              onChange={e => {
                // EDC連携テキスト（smallItem.value）の初期値はnullになっている。
                // フォーム上の入力を消して空文字にした場合、
                // オリジナル（DBに保存されている値）と比較する際に、nullと空文字の比較によって差分が出るのでアラートや公開ボタンの条件が乱れる。
                // よって、フォーム入力が空になった際はvalueをnullにする。
                const value = !!e.target.value ? e.target.value : null

                onChangeSmallItem({
                  smallItemUid: smallItem.uid,
                  values: { value },
                })
              }}
            />
          </FormControl>
        )}
      </Stack>
    )
  },
)

type EDCItemsProps = {
  smallItem: SmallItem
  releaseTried: boolean
  onChangeSmallItem: (value: {
    smallItemUid: string
    values: { [key in keyof SmallItem]?: SmallItem[key] }
  }) => void
}

const EDCItems = memo<EDCItemsProps>(function EDCItems(EDCItemsProps) {
  const { smallItem, releaseTried, onChangeSmallItem } = EDCItemsProps

  return (
    <FormControl>
      <FormLabel>EDCフィールド名</FormLabel>
      {smallItem.itemType === ItemType.CheckBox ? (
        <Value>{smallItem.edcDataItem}</Value>
      ) : (
        <Input
          width="160px"
          value={smallItem.edcDataItem}
          onChange={e =>
            onChangeSmallItem({
              smallItemUid: smallItem.uid,
              values: { edcDataItem: e.target.value },
            })
          }
          className={
            releaseTried && shouldShowError(smallItem.edcDataItem)
              ? 'error'
              : ''
          }
        />
      )}
    </FormControl>
  )
})

type ChoiceListProps = {
  smallItem: SmallItem
  v2: boolean
  hideRolePermission: boolean
}

const ChoiceList: React.FC<ChoiceListProps> = props => {
  const { smallItem, v2, hideRolePermission } = props

  const {
    isClinicalInParent,
    releaseTried,
    smallItemMap,

    onAddChoice,
    onChangeChoice,
    onRemoveChoice,
    onAddSmallItemToChoice,
  } = useContext(CardContext)

  return (
    <ItemContainer>
      <FormLabel>回答の選択肢</FormLabel>
      {smallItem.choices &&
        smallItem.choices.map((choice, choiceIndex) => (
          <ChoiceComponent
            key={choice.uid}
            choiceNumber={choiceIndex + 1}
            presetUsed={smallItem.presetUsed}
            isClinical={isClinicalInParent}
            releaseTried={releaseTried}
            smallItems={
              choice.smallItemUids
                ? choice.smallItemUids.map(uid => smallItemMap[uid])
                : []
            }
            choice={choice}
            itemType={smallItem.itemType}
            v2={v2}
            onChange={({ values }) =>
              onChangeChoice({
                smallItemUid: smallItem.uid,
                choiceIndex,
                values,
              })
            }
            onRemove={() =>
              onRemoveChoice({ smallItemUid: smallItem.uid, choiceIndex })
            }
            onAddSmallItem={() =>
              onAddSmallItemToChoice({
                smallItemUid: smallItem.uid,
                choiceIndex,
              })
            }
            hideRolePermission={hideRolePermission}
          />
        ))}

      {!smallItem.presetUsed && (
        <Button
          leftIcon={<Add />}
          variant="outline"
          onClick={() => onAddChoice({ smallItemUid: smallItem.uid })}
        >
          新規選択肢作成
        </Button>
      )}

      <Spacer size={8} />

      {releaseTried && smallItem.choices.length === 0 && (
        <Text color="red.500" fontSize="sm">
          選択肢を追加してください
        </Text>
      )}
    </ItemContainer>
  )
}
